import cx from 'classnames'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { BrandKit, FontRole, VoiceParams } from 'types'
import Styles from './PreviewCardModal.module.scss'
import { FONT_PAIRINGS, FontPairing } from 'constants/fontPairings'
import { BrandKitPreviewCard } from 'flows/common/steps/BrandKitColorsAndFonts/BrandKitPreviewCard/BrandKitPreviewCard'
import { BrandKitColorSelector } from 'components/BrandKitForm/BrandKitColorSelector/BrandKitColorSelector'
import { ColorPaletteState, toHex } from 'flows/common/steps/BrandKitColorsAndFonts/utils'
import { useEffect, useMemo, useState } from 'react'
import { sample, shuffle } from 'lodash'
import { FontPairingState } from 'flows/common/hooks/useBrandKitState'
import { getBrandKitFontPairing } from 'hooks/queries/brandKits/getBrandKitFontPairing'
import { PALETTES } from 'constants/palettes'
import { BrandKitFonts } from 'flows/common/steps/BrandKitColorsAndFonts/BrandKitFonts'
import { BrandVoiceEditable } from 'screens/BrandKit/BrandVoice/BrandVoiceEditable'
import { useUpdateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useUpdateBrandVoice'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUpdateColorPalette } from 'hooks/queries/workspaces/brandVisualKits/useUpdateColorPalette'
import { useUpsertTextStyle } from 'hooks/queries/workspaces/brandVisualKits/useUpsertTextStyle'
import { BrandKitImagesContent } from 'flows/common/steps/BrandKitImages/BrandKitImages'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { TextInputField } from 'components/Form/TextInputField'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ArrowRefresh2, Plus, Trash2 } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export interface PreviewCardModalProps {
  brandKit: BrandKit
  initialColors?: string[]
  initialColorPaletteState?: ColorPaletteState
  onPaletteSelect?: (palette: string[], index: number) => void
  initialFontPairing?: FontPairing
  onFontPairingChange?: (fontPairing: FontPairing) => void
  reviewBrandVoice?: boolean
  reviewImages?: boolean
  onImagesChange?: (brandKit: BrandKit) => void
  palettes?: string[][]
  onPalettesChange?: (palettes: string[][]) => void
}

export const PreviewCardModal = ({
  brandKit,
  initialColors,
  initialColorPaletteState,
  onPaletteSelect,
  initialFontPairing,
  onFontPairingChange,
  reviewBrandVoice = false,
  reviewImages = false,
  onImagesChange,
  palettes,
  onPalettesChange,
}: PreviewCardModalProps) => {
  const queryClient = useQueryClient()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { closeModal } = useModals()
  const { isMobile } = useResponsiveDesign()

  const kitPairings = useMemo(
    () => (brandKit?.brand_visual_kit ? getBrandKitFontPairing(brandKit.brand_visual_kit) : null) as FontPairing,
    [brandKit],
  )
  const { mutateAsync: updateBrandVoice, isLoading: isUpdatingBrandVoice } = useUpdateBrandVoice({
    id: brandKit.brand_voice?.id ?? 0,
  })

  const { mutateAsync: colorMutation, isLoading: isUpdatingColorPalette } = useUpdateColorPalette({
    brandVisualKitId: brandKit.brand_visual_kit?.id as number,
    id: brandKit.brand_visual_kit?.primary_color_palette.id as number,
  })

  const { mutateAsync: titleMutation, isLoading: isUpdatingTitle } = useUpsertTextStyle({
    brandVisualKitId: brandKit.brand_visual_kit?.id as number,
    fontRole: FontRole.Title,
  })

  const { mutateAsync: bodyMutation, isLoading: isUpdatingBody } = useUpsertTextStyle({
    brandVisualKitId: brandKit.brand_visual_kit?.id as number,
    fontRole: FontRole.Body,
  })

  const isUpdating = isUpdatingBrandVoice || isUpdatingColorPalette || isUpdatingTitle || isUpdatingBody

  const [isRefetching, setIsRefetching] = useState(false)
  const [editingColorPalette, setEditingColorPalette] = useState<string[] | undefined>(undefined)
  const [editingColorPaletteIndex, setEditingColorPaletteIndex] = useState<number | undefined>(undefined)
  const [editingColorIndex, setEditingColorIndex] = useState<number | undefined>(undefined)
  const [voice, setVoice] = useState<VoiceParams | undefined>(brandKit.brand_voice?.voice_params)
  const [colors, setColors] = useState<string[]>(initialColors || [])
  const [colorPaletteState, setColorPaletteState] = useState<ColorPaletteState>(
    initialColorPaletteState || {
      websitePalette: [],
      generatedPalettes: [],
      selectedPaletteIndex: 0,
    },
  )
  const [localProcessedPalettes, setLocalProcessedPalettes] = useState(palettes || [])
  const [errors, setErrors] = useState<number[]>([])
  const [selectedFontPairing, setSelectedFontPairing] = useState<FontPairing>(
    initialFontPairing || kitPairings || sample(FONT_PAIRINGS),
  )
  const [fontPairingState, setFontPairingState] = useState<FontPairingState>({
    websitePairing: null,
  })

  useEffect(() => {
    if (!editingColorPalette) return

    editingColorPalette.forEach((color, index) => {
      if (![7, 4].includes(color.length)) {
        setErrors((prev) => [...prev, index])
      } else {
        setErrors((prev) => prev.filter((i) => i !== index))
      }
    })
  }, [editingColorPalette])

  // Set initial color state of preview card
  useEffect(() => {
    if (!brandKit || colorPaletteState.websitePalette.length) return

    const websitePalette = brandKit.brand_visual_kit?.primary_color_palette?.colors.map((color) => color.hex) || []
    // Generate random palettes at this point
    const generatedPalettes = shuffle(PALETTES).slice(0, 3)

    setColorPaletteState({
      websitePalette,
      generatedPalettes,
      selectedPaletteIndex: 0,
    })

    setColors(localProcessedPalettes[colorPaletteState.selectedPaletteIndex || 0] || [])
  }, [brandKit]) // eslint-disable-line react-hooks/exhaustive-deps

  // Set initial font state of preview card
  useEffect(() => {
    if (!brandKit || fontPairingState.websitePairing?.length) return
    // Save the initial website fonts
    let websitePairing = null

    if (brandKit.brand_visual_kit) {
      websitePairing = getBrandKitFontPairing(brandKit.brand_visual_kit) || null
    }

    setFontPairingState({
      websitePairing,
    })
  }, [brandKit, fontPairingState.websitePairing?.length])

  const handlePaletteSelect = (palette: string[], index: number) => {
    setColors(palette)

    setColorPaletteState((prev) => ({
      ...prev,
      selectedPaletteIndex: index,
    }))
    onPaletteSelect?.(palette, index)
  }

  const handleFontPairingChange = (fontPairing: FontPairing) => {
    setSelectedFontPairing(fontPairing)
    onFontPairingChange?.(fontPairing)
  }

  const handleCancel = () => {
    handlePaletteSelect(initialColors || [], initialColorPaletteState?.selectedPaletteIndex || 0)
    handleFontPairingChange(initialFontPairing || kitPairings || sample(FONT_PAIRINGS))
    closeModal()
  }

  let title = isMobile ? 'Review your Brand Voice' : 'Your Brand Voice is ready'
  if (onPaletteSelect) title = 'Review your colors'
  if (onFontPairingChange) title = 'Review your fonts'
  if (editingColorPalette) title = 'Edit colors'
  if (reviewImages) title = 'Add images and videos to your Brand Kit'

  const isFontTitleDirty =
    initialFontPairing && selectedFontPairing[0].style.font_family !== initialFontPairing[0].style.font_family
  const isFontBodyDirty =
    initialFontPairing && selectedFontPairing[1].style.font_family !== initialFontPairing[1].style.font_family
  const isVoiceDirty =
    voice && reviewBrandVoice && JSON.stringify(voice) !== JSON.stringify(brandKit.brand_voice?.voice_params)
  const isColorDirty = onPaletteSelect && colors.length > 0 && initialColors !== colors

  const handleConfirm = async () => {
    await Promise.all([
      isColorDirty &&
        colorMutation({
          colors: colors.map((color) => ({ hex: toHex(color), name: undefined })),
        }),
      isFontTitleDirty &&
        titleMutation({
          style: selectedFontPairing[0]?.style,
          name: selectedFontPairing[0]?.style.font_family,
        }),
      isFontBodyDirty &&
        bodyMutation({
          style: selectedFontPairing[1]?.style,
          name: selectedFontPairing[1]?.style.font_family,
        }),
      isVoiceDirty && updateBrandVoice({ voice_params: voice }),
    ])

    if (isFontTitleDirty || isFontBodyDirty || isVoiceDirty || isColorDirty) {
      setIsRefetching(true)
      await queryClient
        .refetchQueries({
          predicate: (q) =>
            q.queryKey[0] === 'workspaces' &&
            q.queryKey[1] === currentWorkspaceId &&
            q.queryKey[2] === 'brandKits' &&
            q.queryKey[4] !== null,
        })
        .then(() => {
          setIsRefetching(false)
        })
    }
    closeModal()
  }

  if (editingColorPalette) {
    return (
      <Modal.Root
        onPressOutside={() => {
          setEditingColorPalette(undefined)
          setEditingColorPaletteIndex(undefined)
          setEditingColorIndex(undefined)
        }}
        blaze
        size="md"
      >
        <div className={cx(Styles.previewCardHeader, { [Styles.brandkitImages]: reviewImages })}>
          <Heading level={2}>{title}</Heading>
        </div>
        <Modal.Content>
          <div className={Styles.paletteEditor}>
            <div className={Styles.colors}>
              {editingColorPalette.map((color: string, index: number) => (
                <div key={index} className={Styles.color}>
                  <div className={Styles.colorPreviewContainer}>
                    <div className={Styles.colorPreview} style={{ backgroundColor: color }} />
                    {editingColorPalette.length > 1 && (
                      <div className={Styles.deleteButton}>
                        <IconButton
                          size="sm"
                          variant="tertiary"
                          icon={Trash2}
                          aria-label="Delete color"
                          onPress={() => {
                            const newPalette = editingColorPalette.filter((_, i) => i !== index)
                            setEditingColorPalette(newPalette)
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {editingColorIndex === index ? (
                    <div className={Styles.textInputWrapper}>
                      <TextInputField
                        wrapperClassName={Styles.colorInput}
                        value={color}
                        errorMessage={errors.includes(index) ? 'Invalid hex' : ''}
                        autoFocus
                        onChange={(event) => {
                          if (event.target.value.length > 7) return

                          const newPalette = editingColorPalette.map((c, i) => (i === index ? event.target.value : c))
                          setEditingColorPalette(newPalette)
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className={cx(Styles.colorPill, { [Styles.error]: errors.includes(index) })}
                      onClick={() => setEditingColorIndex(index)}
                    >
                      <Text variant="placeholder" color="var(--dark-80)">
                        {color}
                      </Text>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button
              size="lg"
              variant="tertiary"
              onPress={() => {
                setEditingColorPalette(initialColors || [])
                setEditingColorPaletteIndex(initialColorPaletteState?.selectedPaletteIndex || 0)
                setEditingColorIndex(undefined)
              }}
            >
              Cancel
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <div className={Styles.rightButtons}>
              <Button
                size="lg"
                variant="secondary"
                frontIcon={ArrowRefresh2}
                onPress={() => {
                  setEditingColorPalette(initialColors || [])
                  setEditingColorIndex(undefined)
                }}
              >
                Reset Colors
              </Button>
              <Tooltip label="You can't add more than 5 colors" hide={editingColorPalette.length < 5}>
                <div>
                  <Button
                    size="lg"
                    variant="secondary"
                    frontIcon={Plus}
                    isDisabled={editingColorPalette.length >= 5}
                    onPress={() => {
                      setEditingColorPalette([...editingColorPalette, '#FFFFFF'])
                    }}
                  >
                    Add Color
                  </Button>
                </div>
              </Tooltip>
              <Button
                size="lg"
                isDisabled={errors.length > 0}
                onPress={() => {
                  const updatedLocalProcessedPalettes = localProcessedPalettes.map((palette, index) => {
                    if (index === editingColorPaletteIndex) {
                      return editingColorPalette
                    }
                    return palette
                  })
                  setLocalProcessedPalettes(updatedLocalProcessedPalettes)
                  onPalettesChange?.(updatedLocalProcessedPalettes)
                  if (editingColorPaletteIndex !== undefined) {
                    handlePaletteSelect(editingColorPalette, editingColorPaletteIndex)
                  }
                  setEditingColorPalette(undefined)
                  setEditingColorPaletteIndex(undefined)
                  setEditingColorIndex(undefined)
                }}
              >
                Confirm
              </Button>
            </div>
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )
  }

  return (
    <Modal.Root onPressOutside={handleCancel} blaze size="md" bodyClassName={Styles.previewCardModal}>
      <ResponsiveDesign.Desktop>
        <div className={Styles.previewCardHeader}>
          <Heading level={2}>{title}</Heading>
        </div>
      </ResponsiveDesign.Desktop>
      <Modal.Content>
        <ResponsiveDesign.Mobile>
          <div className={cx(Styles.previewCardHeader, { [Styles.brandKitImages]: reviewImages })}>
            <Heading level={2}>{title}</Heading>
          </div>
        </ResponsiveDesign.Mobile>
        <div className={cx(Styles.previewCardContainer, { [Styles.brandKitImages]: reviewImages })}>
          {!reviewBrandVoice && !reviewImages && (
            <BrandKitPreviewCard colors={colors} fontPairing={selectedFontPairing} brandKit={brandKit} isLogoReadOnly />
          )}

          {(onPaletteSelect || initialFontPairing) && (
            <div className={Styles.previewCardSettings}>
              {onPaletteSelect && (
                <BrandKitColorSelector
                  palettes={localProcessedPalettes}
                  selectedPaletteIndex={colorPaletteState.selectedPaletteIndex}
                  websitePaletteIndex={0}
                  onPaletteSelect={handlePaletteSelect}
                  allowEdit
                  onEdit={(index) => {
                    const palette = localProcessedPalettes[index]
                    if (palette) {
                      setEditingColorPalette(palette.map((c) => toHex(c)))
                      setEditingColorPaletteIndex(index)
                    }
                  }}
                />
              )}

              {onFontPairingChange && (
                <BrandKitFonts
                  fontPairingState={fontPairingState}
                  fontPairing={selectedFontPairing}
                  onFontPairingChange={handleFontPairingChange}
                />
              )}
            </div>
          )}

          {reviewBrandVoice && voice && (
            <div className={Styles.brandVoiceContainer} data-testid="brand-voice">
              <BrandVoiceEditable brandKit={brandKit} onVoiceUpdate={setVoice} isOnboarding={true} />
            </div>
          )}

          {reviewImages && (
            <div className={Styles.brandImagesContainer}>
              <Heading level={3}>Add images and videos</Heading>
              <BrandKitImagesContent
                brandKit={brandKit}
                imagesDescription="We'll use them to quickly generate social posts, videos, blogs, and more."
                imageSize={isMobile ? 95 : 140}
                onImagesChange={onImagesChange}
              />
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <div className={Styles.leftButtons}>
            {!reviewImages && (
              <Button size="lg" variant="tertiary" onPress={handleCancel}>
                Cancel
              </Button>
            )}
          </div>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.rightButtons}>
            <Button size="lg" isDisabled={isUpdating || isRefetching} onPress={handleConfirm}>
              Confirm
            </Button>
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
